import { useState } from 'react';
import { Play } from 'lucide-react';
import { AdminAPI } from '@shared/types/api';
import { useEnqueueAgentRun } from '../../api/useAdminOps';
import { PIP_STYLES, STAGE_SHORT, stageStatusLabel, timeAgo, timeUntil } from './opsUi';

// Event-level stages (event-scoped tasks, e.g. the reservation audit): shown
// once per event in the overview header. The audit is snapshot-only — its
// run report IS the deliverable — so clicking the chip opens the run's
// report; the small play button queues a fresh run. Stage data comes from
// tile.eventStages (identical on every city tile of the event). The enqueue
// still sends a neighborhoodId (schema shape); the server ignores it for
// event-scoped tasks and queues the event-wide run.

export default function EventStageChips({
  eventTimeId,
  anyNeighborhoodId,
  stages,
  onOpenRun,
}: {
  eventTimeId: string;
  anyNeighborhoodId: string;
  stages: AdminAPI.OpsMilestone[];
  onOpenRun: (runId: string) => void;
}) {
  const enqueue = useEnqueueAgentRun();
  const [fired, setFired] = useState(false);

  if (stages.length === 0) return null;

  return (
    <span className="inline-flex items-center gap-1.5 flex-wrap">
      {stages.map((m) => (
        <span
          key={m.task}
          className="inline-flex items-center rounded-md border border-indigo-200 dark:border-indigo-800 overflow-hidden"
        >
          <button
            onClick={() => m.runId && onOpenRun(m.runId)}
            disabled={!m.runId}
            title={`${m.label} — runs ONCE for the entire event: ${stageStatusLabel(m.status)} (${
              new Date(m.plannedAt) > new Date() ? timeUntil(m.plannedAt) : timeAgo(m.plannedAt)
            })${m.runId ? ' — click to open the run report' : ' — no run yet'}`}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium text-indigo-600 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 disabled:cursor-default"
          >
            <span className={`w-2 h-2 rounded-[3px] ${PIP_STYLES[m.status] ?? PIP_STYLES.pending}`} />
            {STAGE_SHORT[m.task] ?? m.task} · event-wide
          </button>
          <button
            onClick={() => {
              setFired(true);
              enqueue.mutate({
                taskType: m.task as AdminAPI.AgentTaskType,
                eventTimeId,
                neighborhoodId: anyNeighborhoodId,
              });
            }}
            disabled={enqueue.isPending}
            title={`Run ${m.label} now (entire event)`}
            className="px-1 py-0.5 border-l border-indigo-200 dark:border-indigo-800 text-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 disabled:opacity-50"
          >
            <Play className="w-2.5 h-2.5" />
          </button>
        </span>
      ))}
      {fired && enqueue.data?.message && (
        <span className="text-[10px] text-dark-muted">{enqueue.data.message}</span>
      )}
      {fired && enqueue.isError && (
        <span className="text-[10px] text-red-600">
          {(enqueue.error as Error)?.message ?? 'failed to queue'}
        </span>
      )}
    </span>
  );
}
